import React, { useState } from "react";
import {
  PaziaFileDropzone,
  PaziaInput,
  PaziaButton,
  PaziaLoader,
} from "components/atoms";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IFile } from "components/atoms";
import { useUploadExtraPage } from "api/hooks";

interface ExtraPageUploadProps {
  courseId: number;
  courseDocumentId: number;
  onUploadSuccess?: () => void;
  nextOrderNumber: number;
  /** Documents 2.0 attach PDF only: the generator cannot use a DOCX page. */
  pdfOnly?: boolean;
}

const isPdf = (file: File) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

const schema = yup.object({
  file: yup.mixed().required("Plik jest wymagany"),
  order: yup.number().required("Kolejność jest wymagana").min(1, "Kolejność musi być większa niż 0"),
});

type FormValues = yup.InferType<typeof schema>;

export const ExtraPageUpload: React.FC<ExtraPageUploadProps> = ({
  courseId,
  courseDocumentId,
  onUploadSuccess,
  nextOrderNumber,
  pdfOnly = false,
}) => {
  const uploadMutation = useUploadExtraPage(courseId, courseDocumentId);
  const [uploadedFile, setUploadedFile] = useState<IFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      order: nextOrderNumber,
    },
  });

  const { handleSubmit, reset, setValue, watch } = methods;

  const onSubmit = async (data: FormValues) => {
    if (!uploadedFile) return;

    try {
      await uploadMutation.mutateAsync({
        file: uploadedFile,
        order: data.order,
      });
      reset();
      setUploadedFile(null);
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleFileDrop = (file: IFile) => {
    if (pdfOnly && !isPdf(file)) {
      setUploadedFile(null);
      setFileError("Do dokumentów 2.0 można dołączyć tylko plik PDF.");
      return;
    }
    setFileError(null);
    setUploadedFile(file);
    setValue("file", file, { shouldValidate: true });
  };

  if (uploadMutation.isPending) {
    return (
      <div className="flex items-center justify-center p-4">
        <PaziaLoader />
        <span className="ml-2">Przesyłanie pliku...</span>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-slate-700">
        <h4 className="text-lg font-semibold">Dodaj dodatkową stronę</h4>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="col-span-1 md:col-span-2">
            <PaziaFileDropzone
              name="file"
              label={pdfOnly ? "Plik (PDF)" : "Plik (PDF lub DOCX)"}
              type={["pdf"]}
              single
              preview="file"
              onDrop={handleFileDrop}
              required
            />
          </div>

          <PaziaInput name="order" label="Kolejność" type="number" className="col-span-1" />

          <div className="col-span-1 flex items-end">
            <PaziaButton
              title="Prześlij plik"
              type="success"
              className="w-full"
              btnType="button"
              disabled={!uploadedFile || uploadMutation.isPending}
              onClick={handleSubmit(onSubmit)}
            />
          </div>
        </div>

        {fileError && <div className="text-sm text-red-600">{fileError}</div>}

        {uploadMutation.isError && (
          <div className="text-sm text-red-600">Błąd podczas przesyłania pliku. Spróbuj ponownie.</div>
        )}
      </div>
    </FormProvider>
  );
};
